import type { ReactNode } from "react";

import { QueryClient } from "@tanstack/react-query";
import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import { routerWithQueryClient } from "@tanstack/react-router-with-query";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import {
  createTRPCClient,
  httpBatchStreamLink,
  loggerLink,
} from "@trpc/client";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import superjson, {
  deserialize,
  serialize,
} from "superjson";

import type { AppRouter } from "@/infrastructure/trpc/router";

import { DefaultCatchBoundary } from "@/infrastructure/components/errors/default-catch-boundary";
import { NotFoundError } from "@/infrastructure/components/errors/not-found-error";
import { TRPCProvider } from "@/infrastructure/trpc/react";
import { getUrl } from "@/infrastructure/trpc/utils";
import { routeTree } from "@/routeTree.gen";

const getRequestHeaders = createServerFn({
  method: "GET",
}).handler(() => {
  const request = getRequest();
  if (!request) {
    return {};
  }

  const headers = new Headers(request.headers);
  const headersObj: Record<string, string> = {};
  headers.forEach((value, key) => {
    headersObj[key] = value;
  });

  return headersObj;
});

export function getRouter() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { staleTime: 30 * 1000 },
      dehydrate: { serializeData: serialize },
      hydrate: { deserializeData: deserialize },
    },
  });

  const trpcClient = createTRPCClient<AppRouter>({
    links: [
      loggerLink({
        enabled: (op) =>
          import.meta.env.MODE === "development" ||
          (op.direction === "down" &&
            op.result instanceof Error),
      }),
      httpBatchStreamLink({
        transformer: superjson,
        url: getUrl(),
        async headers() {
          const requestHeaders = await getRequestHeaders();
          return {
            ...requestHeaders,
            // Bypass ngrok browser warning in development
            "ngrok-skip-browser-warning": "true",
          };
        },
      }),
    ],
  });

  const trpc = createTRPCOptionsProxy<AppRouter>({
    client: trpcClient,
    queryClient,
  });

  const router = createTanStackRouter({
    context: { queryClient, trpc },
    routeTree,
    defaultPreload: "intent",
    defaultErrorComponent: DefaultCatchBoundary,
    defaultNotFoundComponent: () => <NotFoundError />,
    scrollRestoration: true,
    trailingSlash: "never",
    Wrap: (props: { children: ReactNode }) => {
      return (
        <TRPCProvider
          trpcClient={trpcClient}
          queryClient={queryClient}
        >
          {props.children}
        </TRPCProvider>
      );
    },
  });

  return routerWithQueryClient(router, queryClient);
}
